import { useState, useEffect, useRef } from 'react';
import { useSignalR } from '@hooks/useSignalR';
import { apiClient } from '@services/api';
import { ACTIVITY_CHANGED_EVENT } from '@utils/activityEvents';
import './ActiveUpdates.scss';

interface ActiveItem {
  type: string;
  referenceId: string;
  status: string;
  title: string;
  icon: string;
  detail: string;
  createdAt: string;
  updatedAt: string;
  requestedBy?: string;
}

const TYPE_CONFIG: Record<string, { icon: string; label: string }> = {
  order: { icon: '🍽️', label: 'Food Order' },
  housekeeping: { icon: '🛎️', label: 'Housekeeping' },
  maintenance: { icon: '🔧', label: 'Maintenance' },
  laundry: { icon: '👔', label: 'Laundry' },
  service: { icon: '🔔', label: 'Service Request' },
  reservation: { icon: '📅', label: 'Reservation' },
};

const STATUS_COLORS: Record<string, string> = {
  Pending: '#f59e0b', Acknowledged: '#3b82f6', Confirmed: '#3b82f6',
  Preparing: '#8b5cf6', Ready: '#10b981', InProgress: '#3b82f6',
  'In Progress': '#3b82f6', Reported: '#f59e0b', OnHold: '#ef4444',
  Sent: '#3b82f6', Received: '#8b5cf6',
};

interface Props {
  hotelSlug?: string | null;
}

/** The guest's last active list, for the session (token) it was loaded for. */
let lastList: { token: string | null; items: ActiveItem[] } | null = null;
const currentToken = () => { try { return localStorage.getItem('gcxp_token'); } catch { return null; } };

export default function ActiveUpdates({ hotelSlug }: Props) {
  const { on } = useSignalR();
  // Last list shown, so coming back to the welcome page shows it at once instead of placeholders that
  // collapse (and move the page) when there is nothing active.
  const cachedItems = lastList && lastList.token === currentToken() ? lastList.items : null;
  const [items, setItems] = useState<ActiveItem[]>(cachedItems ?? []);
  const [loading, setLoading] = useState(cachedItems === null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadAll = async () => {
    try {
      const { data } = await apiClient.get('/api/v1/guest/activity');
      const active: any[] = data?.active || [];

      const mapped: ActiveItem[] = active.map((a: any) => {
        const cfg = TYPE_CONFIG[a.type] || { icon: '📋', label: a.type };
        let detail = '';
        if (a.type === 'order') {
          detail = (a.items || []).map((i: any) => `${i.quantity}x ${String(i.name || 'Item')}`).join(' · ');
        } else if (a.type === 'housekeeping') {
          detail = a.itemName ? `${a.quantity || 1}x ${a.itemName}` : (a.requestType || '');
        } else if (a.type === 'maintenance') {
          detail = a.subject || '';
        } else if (a.type === 'service') {
          detail = a.description || a.category || '';
        } else if (a.type === 'reservation') {
          detail = `${a.reservationType || ''} ${a.date || ''} ${a.time || ''}`.trim();
        } else if (a.type === 'laundry') {
          detail = (a.items || []).map((i: any) => `${i.quantity}x ${String(i.itemName || i.name || 'Item')}`).join(' · ');
        }

        return {
          type: a.type,
          referenceId: a.referenceId,
          status: a.status,
          title: a.stationName || cfg.label,
          icon: cfg.icon,
          detail,
          createdAt: a.createdAt,
          updatedAt: a.updatedAt || a.createdAt,
          requestedBy: a.requestedBy,
        };
      });

      // Sort by last updated, most recent first
      mapped.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

      // Detect prepend: if the top card changed (or this is the first new card appearing),
      // scroll the carousel back to the first card so the user sees the latest activity.
      setItems(prev => {
        const prevTopId = prev[0] ? `${prev[0].type}-${prev[0].referenceId}` : null;
        const nextTopId = mapped[0] ? `${mapped[0].type}-${mapped[0].referenceId}` : null;
        const topChanged = prevTopId !== nextTopId;
        if (topChanged && mapped.length > 0) {
          setPage(0);
          // Defer scroll to after the new items render
          requestAnimationFrame(() => {
            // Scroll the carousel only — scrollIntoView would also move the page (a jump a second after it opens).
            scrollRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
          });
        } else {
          setPage(p => p >= mapped.length ? 0 : p);
        }
        lastList = { token: currentToken(), items: mapped };
        return mapped;
      });
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, [hotelSlug]);

  // Reload on visibility change (tab switch, returning to browser)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadAll();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [hotelSlug]);

  // SignalR: reload on any status change, or on new chat messages that actually
  // carry a new activity (menu/laundry picker or status tracker payload).
  // Plain text messages don't affect the activity list.
  useEffect(() => {
    const carriesActivity = (raw: unknown): boolean => {
      const metadata = (raw as { metadata?: string | null })?.metadata;
      if (!metadata) return false;
      try {
        const parsed = JSON.parse(metadata);
        return !!parsed && (parsed.type === 'menu' || parsed.type === 'laundry_menu' || !!parsed.referenceId);
      } catch { return false; }
    };
    const unsubs = [
      on('OrderStatusChanged', () => loadAll()),
      on('RequestStatusChanged', () => loadAll()),
      on('ReportStatusChanged', () => loadAll()),
      on('LaundryStatusChanged', () => loadAll()),
      on('ServiceRequestStatusChanged', () => loadAll()),
      on('ReservationStatusChanged', () => loadAll()),
      on('NewMessage', (raw) => { if (carriesActivity(raw)) loadAll(); }),
    ];
    return () => unsubs.forEach(u => u());
  }, [on, hotelSlug]);

  // Local events — fired when the same device places an order (no SignalR round-trip)
  useEffect(() => {
    const handler = () => loadAll();
    window.addEventListener(ACTIVITY_CHANGED_EVENT, handler);
    return () => window.removeEventListener(ACTIVITY_CHANGED_EVENT, handler);
  }, [hotelSlug]);

  // Track scroll position for dot indicator
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const children = Array.from(el.children) as HTMLElement[];
      if (children.length === 0) return;
      let closest = 0;
      let minDist = Infinity;
      children.forEach((child, i) => {
        const dist = Math.abs(child.offsetLeft - el.scrollLeft);
        if (dist < minDist) { minDist = dist; closest = i; }
      });
      setPage(closest);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [items.length]);

  // Loading skeleton
  if (loading) {
    return (
      <div className="active-updates mb-4">
        <div className="active-updates-header">
          <span className="active-updates-title">Active Updates</span>
        </div>
        <div className="active-updates-scroll">
          {[1, 2].map(i => (
            <div key={i} className="active-update-card placeholder-glow" style={{ flex: '0 0 85%' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="placeholder rounded-circle" style={{ width: '1.5rem', height: '1.5rem' }} />
                <span className="placeholder col-4" style={{ height: '0.85rem' }} />
                <span className="placeholder col-2 ms-auto rounded-pill" style={{ height: '0.75rem' }} />
              </div>
              <span className="placeholder col-8 d-block mb-1" style={{ height: '0.75rem' }} />
              <span className="placeholder col-3" style={{ height: '0.6rem', opacity: 0.5 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) return null;

  const scroll = (dir: number) => {
    const next = Math.max(0, Math.min(page + dir, items.length - 1));
    setPage(next);
    // Centre the card by scrolling the carousel sideways only (relative, so it works in RTL too).
    const el = scrollRef.current;
    const card = el?.children[next] as HTMLElement | undefined;
    if (el && card) {
      const box = el.getBoundingClientRect();
      const c = card.getBoundingClientRect();
      el.scrollBy({ left: c.left + c.width / 2 - (box.left + box.width / 2), behavior: 'smooth' });
    }
  };

  const formatTime = (iso: string) => {
    try {
      const d = new Date(iso);
      const now = new Date();
      const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const formatDateTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div className="active-updates mb-4">
      <div className="active-updates-header">
        <span className="active-updates-title">Active Updates</span>
        <span className="active-updates-count">{items.length}</span>
        {items.length > 1 && (
          <div className="active-updates-nav">
            <button className="nav-btn" onClick={() => scroll(-1)} disabled={page === 0}>‹</button>
            <span className="nav-dots">
              {items.map((_, i) => (
                <span key={i} className={`dot ${i === page ? 'active' : ''}`} />
              ))}
            </span>
            <button className="nav-btn" onClick={() => scroll(1)} disabled={page === items.length - 1}>›</button>
          </div>
        )}
      </div>
      <div className="active-updates-scroll" ref={scrollRef}>
        {items.map((item) => {
          const key = `${item.type}-${item.referenceId}`;
          const isExpanded = expandedId === key;
          const color = STATUS_COLORS[item.status] || '#6b7280';
          return (
            <div
              key={key}
              className={`active-update-card ${isExpanded ? 'expanded' : ''}`}
              onClick={() => setExpandedId(isExpanded ? null : key)}
            >
              <div className="update-card-top">
                <span className="update-icon">{item.icon}</span>
                <span className="update-title">{item.title}</span>
                <span className="update-status" style={{ background: color + '20', color }}>
                  {item.status}
                </span>
              </div>
              {item.detail && (
                <div className="update-items">{item.detail}</div>
              )}
              <div className="update-time">
                {formatTime(item.updatedAt)}
                {item.requestedBy && <span className="update-person"> · {item.requestedBy}</span>}
              </div>
              {isExpanded && (
                <div className="update-detail">
                  <div className="update-detail-row">
                    <span className="update-detail-label">Started</span>
                    <span>{formatDateTime(item.createdAt)}</span>
                  </div>
                  {item.updatedAt !== item.createdAt && (
                    <div className="update-detail-row">
                      <span className="update-detail-label">Last updated</span>
                      <span>{formatDateTime(item.updatedAt)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
